// @material-ui/core components
import {
  // Button, Icon, InputAdornment,
  ClickAwayListener,
  Icon,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  TextField,
} from '@material-ui/core';
import { debounce } from 'lodash-es';
import React, { MouseEvent } from 'react';

import { IModel } from '~/src/dash/components/model';
import { IBaseState, IWithStylesProps } from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import {
  blackColor,
  iconButton,
  primaryColor,
  whiteColor,
} from '~/src/dash/utils/material-dashboard-react';

export interface IProps {
  clear?: boolean;
  model?: IModel;
  models?: IModel[];
  defaultText?: string;
  maskModel?: boolean;
  maskedModelPrefix?: string;

  label: React.ReactNode | string;
  helperText?: string;
  searchHelperText?: string;
  onSelect?: (model?: IModel) => void;
  query?: (query: string) => Promise<IModel[]>;

  TextFieldProps?: any;
  InputLabelProps?: any;
  InputProps?: any;
}

export interface IState extends IBaseState {
  openSearch: boolean;
  existingQuery: string;
  searchInprogress: boolean;
  countInprogress: number;

  model?: IModel;
  models: IModel[];
}

export const ModelSelectView = decorate(
  {
    router: true,
    styles: () => ({
      searchInputRoot: {
        '&:before': {
          borderBottomColor: blackColor,
        },
        '&:after': {
          borderBottomColor: primaryColor[0],
        },
      },

      popper: {
        background: whiteColor,
        'z-index': 100,
        width: '100%',
        marginLeft: '18px',
        top: '0px',
      },
      clearButton: iconButton,
    }),
  },
  class extends React.Component<IProps & IWithStylesProps, IState> {
    state = {
      openSearch: false,
      existingQuery: '',
      searchInprogress: false,
      countInprogress: 0,

      model: this.props.model,
      models: (this.props.models || []) as IModel[],

      maskModel: !!this.props.maskModel,
      maskedModelPrefix: this.props.maskedModelPrefix || 'demo',
    };

    searchInputRef = React.createRef();
    anchorEl: HTMLInputElement;

    handleCloseSearch = debounce(() => {
      if (this.state.existingQuery !== '') {
        this.requestModels('');
      }
      this.setState({
        existingQuery: '',
        openSearch: false,
      });
    }, 100);

    _requestModels = debounce((query: string, countInprogress: number) => {
      if (this.state.countInprogress !== countInprogress) {
        return;
      }
      if (this.props.query) {
        this.props.query(query).then(
          (models: IModel[]) => {
            if (this.state.countInprogress !== countInprogress) {
              return;
            }
            this.setState({
              searchInprogress: false,
              models,
            });
          },
          () => {
            if (this.state.countInprogress !== countInprogress) {
              return;
            }
            this.setState({
              searchInprogress: false,
            });
          },
        );
      }
    }, 500);

    requestModels = (query: string) => {
      const countInprogress = this.state.countInprogress + 1;
      this.setState({
        searchInprogress: true,
        countInprogress,
      });

      this._requestModels(query, countInprogress);
    };

    handleOpenSearch = () => {
      this.setState({
        openSearch: true,
      });

      if ('focus' in this.searchInputRef) {
        // @ts-ignore
        this.searchInputRef.focus();
      }
    };

    selectModel = ({ currentTarget }: MouseEvent<HTMLLIElement>) => {
      const model = this.state.models.filter((model: IModel) => {
        return model.ID === currentTarget.dataset.value;
      })[0];
      if (this.props.onSelect) {
        this.props.onSelect(model);
      }
      this.setState({
        model,
        openSearch: false,
      });
    };

    clearSelected = () => {
      if (this.props.onSelect) {
        this.props.onSelect(undefined);
      }
      this.setState({
        site: undefined,
        openSearch: false,
      });
    };

    onChange = ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
      const query = currentTarget.value.trim();
      this.setState({
        existingQuery: query,
      });

      this.requestModels(query);
    };

    componentDidMount() {
      this.requestModels(''); // query with empty string to request all publishers with same reseller id
    }

    componentDidUpdate() {
      if (this.props.model !== this.state.model) {
        this.setState({
          model: this.props.model,
        });
      }
    }

    render() {
      const { classes, TextFieldProps, InputLabelProps, InputProps } = this.props;
      return (
        <div>
          <div>
            <TextField
              {...TextFieldProps}
              label={this.props.label}
              helperText={this.props.helperText}
              value={
                this.state.model
                  ? this.state.maskModel
                    ? `${this.state.maskedModelPrefix}_${this.state.model.ID}`
                    : `[${this.state.model.ID}] ${this.state.model.Name}`
                  : this.props.defaultText !== undefined
                  ? this.props.defaultText
                  : 'All'
              }
              onClick={this.handleOpenSearch}
              margin='normal'
              InputLabelProps={InputLabelProps}
              InputProps={{
                ...InputProps,
                inputRef: (node: HTMLInputElement) => {
                  this.anchorEl = node;
                },
                endAdornment: this.props.clear && (
                  <Icon classes={{ root: classes.clearButton }} onClick={this.clearSelected}>
                    close
                  </Icon>
                ),
              }}
            />
          </div>
          <Popper
            open={this.state.openSearch}
            anchorEl={this.anchorEl}
            placement='bottom-start'
            disablePortal
            className={classes.popper}
          >
            <Paper>
              <ClickAwayListener onClickAway={this.handleCloseSearch}>
                <div style={{ padding: '1rem' }}>
                  <TextField
                    InputProps={{
                      ref: this.searchInputRef,
                      autoFocus: true,
                      classes: {
                        root: classes.searchInputRoot,
                      },
                    }}
                    onChange={this.onChange}
                    className={classes.input}
                    fullWidth
                    helperText={this.props.searchHelperText}
                  />
                </div>
              </ClickAwayListener>
              <MenuList style={{ minHeight: '400px', maxHeight: '50vh', overflowX: 'scroll' }}>
                {this.state.searchInprogress ? (
                  <MenuItem disableRipple disableTouchRipple>
                    Searching...
                  </MenuItem>
                ) : (
                  this.state.models.map((model: IModel) => {
                    return (
                      <MenuItem
                        key={'k_' + model.ID}
                        onClick={this.selectModel}
                        data-value={model.ID}
                      >
                        {this.state.maskModel
                          ? `${this.state.maskedModelPrefix}_${model.ID}`
                          : `[${model.ID}] ${model.Name}`}
                      </MenuItem>
                    );
                  })
                )}
              </MenuList>
            </Paper>
          </Popper>
        </div>
      );
    }
  },
) as React.ComponentType<IProps>;
